import React, { useState } from 'react';
import styled from 'styled-components';
import TrueImpactLoading from 'shared/components/TrueImpactLoading';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import TodoTasks from './TodoTasks';
import ThirdPartyTodoTasks, { ManageProgramsAndReportsLink } from './ThirdPartyTodoTasks';
import { useThirdPartyTodoActionable } from './ThirdPartyTodoTasksUtils';
import { useUserActions } from 'shared/auth/hooks/useUserActions';
import { User } from 'donor/portal/pages/FeedbackDashboard/Feedback/utils';
import { useUser } from 'shared/auth/context';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { AllocationReportCreationProps } from './Allocation/AllocationReportCreation';
import TrueImpactDebug from '../ReportBuilder/TrueImpactDebug';
import TrueImpactButton from 'shared/design-system/components/TrueImpactButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLightbulb } from '@fortawesome/pro-light-svg-icons';
import ClaimCodeDialog from './ClaimCodeDialog';
import ReportSharingModal from 'shared/account/pages/reportSharing/ReportSharingModal';
import { useFeatureFlags } from 'shared/providers/FeatureFlags/useFeatureFlags';
import { useTaskContext } from './TaskProvider';

interface TodoProps {
  allReports: AllocationReportCreationProps['allReports'];
}

const StyledDiv = styled.div`
  padding: 48px;
  .your-tasks-heading {
    text-align: center;
  }
  .claim-code-cta {
    max-width: 725px;
    margin: ${SPACERS.medium} auto 0 auto;
    display: flex;
    justify-content: flex-end;
  }
  .tasks-card {
    max-width: 725px;
    margin-left: auto;
    margin-right: auto;
    border-radius: 4px;
    padding: ${SPACERS.tiny} ${SPACERS.large} ${SPACERS.large} ${SPACERS.large};
    margin-top: ${SPACERS.medium};
    border: 1px solid ${COLORS.grayLight};
    background-color: ${COLORS.whiteBase};
  }
`;

const Todo: React.FC<TodoProps> = ({ allReports }) => {
  const user = useUser();
  const { isBBUser, isCNUser } = useUserActions();
  const [reportVisibility, claimAnInvestment, cnTaskUpdates] = useFeatureFlags(
    'reportVisibility',
    'claimAnInvestment',
    'cnTaskUpdates',
  );
  const isThirdPartyTodo = isBBUser || (isCNUser && cnTaskUpdates);
  const thirdPartyTodo = useThirdPartyTodoActionable(isThirdPartyTodo);
  const thirdPartyActionable = thirdPartyTodo.actionable;
  const [debug, setDebug] = useState<boolean>(false);
  const [showClaimDialog, setShowClaimDialog] = useState<boolean>(false);

  const {
    tasks,
    taskReports,
    allNPOUsers,
    allPrograms,
    reloadTasks,
    reloadPrograms,
    reloadNPOUser,
    requestStatus,
    reportsStatus,
    unallocatedStatus,
    hasError,
  } = useTaskContext();

  const userAttributes = (user.attributes as unknown) as User | undefined;
  const name = userAttributes?.givenName || userAttributes?.given_name;

  if (
    requestStatus === 'REQUEST_INIT' ||
    requestStatus === 'REQUEST_START' ||
    reportsStatus === 'REQUEST_INIT' ||
    reportsStatus === 'REQUEST_START' ||
    unallocatedStatus === 'REQUEST_INIT' ||
    unallocatedStatus === 'REQUEST_START' ||
    (isThirdPartyTodo && !thirdPartyTodo.loaded)
  ) {
    return <TrueImpactLoading />;
  }

  if (hasError) {
    return (
      <StyledDiv>
        <TrueImpactText color="critical">
          Unable to load the tasks. Please try refreshing the page.
        </TrueImpactText>
      </StyledDiv>
    );
  }

  const noOutstandingTasks =
    tasks.thisWeek.length === 0 &&
    tasks.thisMonth.length === 0 &&
    tasks.nextMonth.length === 0 &&
    tasks.incomplete.length === 0 &&
    tasks.reportTasks.length === 0 &&
    tasks.finalUpdateTasks.length === 0 &&
    tasks.interimUpdateTasks.length === 0 &&
    tasks.unallocatedInvestmentTasks.length === 0;

  return (
    <>
      {reportVisibility && <ReportSharingModal />}
      <StyledDiv>
        <TrueImpactDebug isDebug={debug} handleChange={setDebug} />
        <div className="your-tasks-heading">
          <TrueImpactText variant="heading-page">
            Welcome back {name}!
          </TrueImpactText>
          <TrueImpactText>
            Here are <strong>your</strong> tasks:
          </TrueImpactText>
        </div>
        {!isBBUser && claimAnInvestment && (
          <div className="claim-code-cta">
            <TrueImpactButton
              variant="secondary-small"
              onClick={() => setShowClaimDialog(true)}
            >
              <FontAwesomeIcon icon={faLightbulb} /> Claim an Investment
            </TrueImpactButton>
          </div>
        )}
        <ClaimCodeDialog
          open={showClaimDialog}
          onClose={() => setShowClaimDialog(false)}
        />
        {isThirdPartyTodo &&
          (thirdPartyActionable ? (
            <ThirdPartyTodoTasks
              className="tasks-card"
              programs={thirdPartyTodo.programs}
              reloadPrograms={thirdPartyTodo.reload}
            />
          ) : (
            <ManageProgramsAndReportsLink />
          ))}
        {!(noOutstandingTasks && isThirdPartyTodo && thirdPartyActionable) && (
          <div className="tasks-card">
            <TodoTasks
              debug={debug}
              taskReports={taskReports}
              userAttributes={userAttributes}
              noOutstandingTasks={noOutstandingTasks}
              tasks={tasks}
              allReports={allReports}
              allPrograms={allPrograms}
              allNPOUsers={allNPOUsers}
              reloadTasks={reloadTasks}
              reloadPrograms={reloadPrograms}
              reloadNPOUser={reloadNPOUser}
            />
          </div>
        )}
      </StyledDiv>
    </>
  );
};

export default Todo;
